import React, { useState } from 'react'
import FormGroup from 'react-bootstrap/lib/FormGroup'
import FormControl from 'react-bootstrap/lib/FormControl'
import DropdownButton from 'react-bootstrap/lib/DropdownButton'
import Switch from '../../../utilities/Switch'
import FrappModal from '../../../utilities/FrappModal'

/*
 * Read-only counterparts to DataCollectionForm.jsx and TriggerActionForm.jsx.
 *
 * Uses the builder's own markup and controls - question-form, each-feeback,
 * title-section, dc single-tab, FormGroup/FormControl - with every control
 * disabled, so it renders identically rather than merely similarly. Removed:
 * the reorder arrows, add/delete option, Bulk list search, the file upload,
 * the Optional toggle, + Add Variable, + Add More and SAVE.
 */

const typeLabel = (inputType) => {
    switch (inputType) {
        case 'singleselect': return 'Single Select'
        case 'multiselect': return 'Multi Select'
        case 'text': return 'Text'
        case 'date-time':
        case 'date':
        case 'time': return 'Date & Time'
        default: return inputType
    }
}

const perLanguage = (scripts, nodeId, kind) => (scripts || []).map(script => {
    const element = (script.script || []).find(item => item.id === nodeId)
    const data = (element && element.data) || {}
    return { name: script.scriptLanguage, items: (kind === 'form' ? data.form : data.actions) || [] }
})

const Question = ({ question, index }) => {
    const inputType = question.questionType || question.inputType
    const isSelect = inputType === 'singleselect' || inputType === 'multiselect'
    const options = question.options || []

    return (
        <div className="question-form">
            <div className="each-feeback">
                <div className='title-section'>
                    <div>Question {index + 1}</div>
                </div>

                <FormGroup className="label">
                    <FormControl className="qs-title" disabled readOnly
                        value={question.question || question.label || ''} />
                </FormGroup>

                <div className="tabs-wrapper">
                    <div className="datetime-dropdown">
                        <DropdownButton title={typeLabel(inputType)} id={`snapshot-type-${index}`} disabled />
                    </div>

                    {inputType === 'text' && question.validationType ?
                        <div className="text-validation">
                            <DropdownButton title={question.validationType}
                                id={`snapshot-validation-${index}`} disabled />
                        </div> : null}
                </div>

                {isSelect && options.length > 0 ?
                    <div className="options-form-wrapper">
                        {options.map((option, i) => (
                            <div className="input-option-div" key={i}>
                                <FormGroup className="label">
                                    <FormControl className="qs-title" disabled readOnly
                                        value={option.optionText || ''} />
                                </FormGroup>
                            </div>
                        ))}
                    </div> : null}

                {isSelect ?
                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0' }}>
                            <input type="checkbox" checked={!!question.includeOthers} disabled readOnly />
                            <span>Others</span>
                        </label>
                    </div> : null}
            </div>

            {/* The builder's footer row, keeping the Optional switch as a
                readout and dropping Delete Question. */}
            <div className="bottom-btns">
                <div className="switch-optional">
                    <p>Optional</p>
                    <Switch checked={!!question.isOptional} onToggle={() => {}} name={`snapshot-optional-${index}`} />
                </div>
            </div>
        </div>
    )
}

/*
 * Mirrors TriggerActionForm's grey-box-wrapper layout: Title / Url / Tag as
 * group-label-input rows, the tag as a pill, and variables inside the dotted
 * border. Removed: the tag input, the per-variable delete, + ADD VARIABLE,
 * the action delete, +ADD MORE and the submit button.
 */
const Action = ({ action }) => {
    //Stored as bodyParams on the doc; the builder edits them as variables[].
    const variables = action.variables
        || Object.keys(action.bodyParams || {}).map(key => ({ name: key, value: action.bodyParams[key] }))

    return (
        <div className='grey-box-wrapper'>
            <div className='text-content'>
                <div className='form-data'>
                    <FormGroup>
                        <div className='group-label-input'>
                            <span className='label-bold'>Title</span>
                            <FormControl className="qs-title" value={action.title || ''} disabled readOnly />
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <div className='group-label-input'>
                            <span className='label-bold'>Url</span>
                            <FormControl className="qs-title" value={action.api || ''} disabled readOnly />
                        </div>
                    </FormGroup>

                    <div className='group-label-input'>
                        <span className='label-bold'>Tag</span>
                        <div className='tags-section'>
                            {action.tag ?
                                <div className="pill-wrapper">
                                    <div className="pill">{action.tag}</div>
                                </div> : null}
                        </div>
                    </div>

                    {variables.length > 0 ?
                        <div className='name-value-section'>
                            <div className='dotted-border bg-white'>
                                <div className='contain'>
                                    {variables.map((item, varIndex) => (
                                        <div className='name-value-row' key={varIndex}>
                                            <div className='label-value'>
                                                <div className='label-bold'>{varIndex + 1}. Key</div>
                                                <FormControl className="qs-title" value={item.name || ''} disabled readOnly />
                                            </div>
                                            <div className='label-value w-70'>
                                                <span className='label-bold w-10'>Value</span>
                                                <div style={{ width: '100%' }}>
                                                    <FormControl className="qs-title" value={String(item.value || '')} disabled readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div> : null}
                </div>
            </div>
        </div>
    )
}

const SnapshotForm = ({ kind, nodeId, scripts, closePopup }) => {
    const languages = perLanguage(scripts, nodeId, kind)
    const [activeLanguage, setActiveLanguage] = useState(languages.length > 0 ? languages[0].name : '')

    const active = languages.find(language => language.name === activeLanguage)
    const items = (active && active.items) || []

    //FrappModal at bsSize large with the heading inside .caller-qc - the same
    //container the builder uses, which is where the width comes from.
    //uicomponents/popup is a different, narrower modal.
    return (
        <FrappModal
            bsSize={'large'}
            onCloseButton={true}
            className="caller-audit-modal modal-scrollbox"
            show={true}
            closeButtonClicked={closePopup}
        >
            <div className="caller-qc">
                <div className="heading">{kind === 'form' ? 'Data Collection' : 'Trigger Action'}</div>
                <div className="additional-wrapper2 snapshot-readonly" style={{ padding: '2%' }}>
                    <div className="outcome-wrapper">
                        <div className="feedback-form">
                            {/* .dc.single-tab is width:10%, so it only lays out
                                inside the builder's own flex row - Data Collection
                                uses this inline style, Trigger Action uses
                                tab-wrapper > tab-container. */}
                            <div className={kind === 'form' ? '' : 'tab-wrapper'}
                                style={kind === 'form' ? {
                                    display: 'flex', flexFlow: 'row', width: '100%', gap: '10px',
                                    marginBottom: '20px', alignItems: 'center', backgroundColor: '#fff',
                                    padding: '10px 0', position: 'sticky', top: 0, zIndex: 100,
                                    borderBottom: '1px solid #eee'
                                } : undefined}>
                                <div className={kind === 'form' ? '' : 'tab-container'}
                                    style={kind === 'form' ? { display: 'contents' } : undefined}>
                                {languages.map(language => (
                                    <div className='dc single-tab' key={language.name}
                                        onClick={() => setActiveLanguage(language.name)}>
                                        <div>
                                            <a>
                                                <div className={language.name === activeLanguage ? 'each-tab active' : 'each-tab'}>
                                                    {language.name}({language.items.length})
                                                </div>
                                            </a>
                                        </div>
                                    </div>
                                ))}
                                </div>
                            </div>

                            {items.length === 0
                                ? <p className="snapshot-readonly-empty">Nothing recorded for this language.</p>
                                : items.map((item, index) => (
                                    kind === 'form'
                                        ? <Question key={index} question={item} index={index} />
                                        : <Action key={index} action={item} />
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        </FrappModal>
    )
}

export default SnapshotForm
